import React, { useState, useEffect } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import { X } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import callApi from "Common/utils/apiCaller";

const AdminForm = () => {
  document.title = "Add Admin | SuperSalesIQ";
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [adminData, setAdminData] = useState<any>(null);

  useEffect(() => {
    // Check if we're in edit mode (URL contains an ID)
    const searchParams = new URLSearchParams(location.search);
    const adminId = searchParams.get("id");
    
    if (adminId) {
      setIsEdit(true);
      fetchAdminData(adminId);
    }
  }, [location]);

  const fetchAdminData = async (adminId: string) => {
    setLoading(true);
    try {
      const res = await callApi(`v1/admin/${adminId}`, "get");
      if (res && res.data && res.data.data) {
        setAdminData(res.data.data);
        validation.setValues({
          contact_name: res.data.data.contact_name || "",
          contact_email: res.data.data.contact_email || "",
          contact_phone: res.data.data.contact_phone || "",
          gender: res.data.data.gender || "",
          password: "", // Don't set password in edit mode
        });
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast("Failed to fetch admin data");
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      gender: "",
      password: "",
    },
    validationSchema: Yup.object({
      contact_name: Yup.string().required("Please Enter Name"),
      gender: Yup.string().required("Please Enter Gender"),
      contact_email: Yup.string().email("Invalid email format").required("Please Enter Email"),
      contact_phone: Yup.string().required("Please Enter Phone"),
      password: isEdit ? Yup.string() : Yup.string().required("Please Enter Password"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (isEdit) {
          // Update existing admin
          const res = await callApi(`v1/admin/${adminData._id}/update`, "post", {
            admin: {
              contact_name: values.contact_name,
              contact_email: values.contact_email,
              contact_phone: values.contact_phone,
              gender: values.gender,
              ...(values.password && { password: values.password }),
            },
          });
          
          if (res && res.data && res.data.status === "Success") {
            toast("Admin updated successfully");
            setTimeout(() => {
              navigate("/");
            }, 1500);
          } else {
            toast("Failed to update admin");
          }
        } else {
          // Create new admin
          const res = await callApi(`v1/admin/signup`, "post", {
            admin: {
              contact_name: values.contact_name,
              contact_email: values.contact_email,
              contact_phone: values.contact_phone,
              gender: values.gender,
              password: values.password,
            },
          });
          
          if (res && res.data && res.data.status === "Success") {
            toast("Admin created successfully");
            setTimeout(() => {
              navigate("/");
            }, 1500);
          } else {
            toast("Failed to create admin");
          }
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        toast("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <React.Fragment>
      <BreadCrumb title={isEdit ? "Edit Admin" : "Add Admin"} pageTitle="Admins" />
      <ToastContainer closeButton={false} limit={1} />
      
      <div className="grid grid-cols-12 gap-x-5">
        <div className="col-span-12 card 2xl:col-span-12">
          <div className="card-body">
            <div className="flex items-center">
              <h6 className="text-15 grow">{isEdit ? "Edit Admin" : "Add Admin"}</h6>
              <div className="shrink-0">
                <Link
                  to="/"
                  className="text-slate-500 btn bg-slate-200 border-slate-200 hover:text-slate-600 hover:bg-slate-300 hover:border-slate-300 focus:text-slate-600 focus:bg-slate-300 focus:border-slate-300 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-300 active:border-slate-300 active:ring active:ring-slate-100 dark:bg-zink-600 dark:hover:bg-zink-500 dark:border-zink-600 dark:hover:border-zink-500 dark:text-zink-200 dark:ring-zink-400/20"
                >
                  <X className="inline-block size-4" />{" "}
                  <span className="align-middle">Cancel</span>
                </Link>
              </div>
            </div>
          </div>
          <div className="card-body">
            {loading && !validation.isSubmitting ? (
              <div className="flex items-center justify-center h-60">
                <TailSpin visible={true} height="40" width="40" color="#4fa94d" />
              </div>
            ) : (
              <form
                className="create-form"
                onSubmit={validation.handleSubmit}
              >
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="contact_name"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Name*
                    </label>
                    <input
                      type="text"
                      id="contact_name"
                      name="contact_name"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter name"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.contact_name}
                    />
                    {validation.touched.contact_name &&
                    validation.errors.contact_name ? (
                      <p className="text-red-500">
                        {validation.errors.contact_name}
                      </p>
                    ) : null}
                  </div>
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="contact_email"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Email*
                    </label>
                    <input
                      type="email"
                      id="contact_email"
                      name="contact_email"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter email"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.contact_email}
                    />
                    {validation.touched.contact_email &&
                    validation.errors.contact_email ? (
                      <p className="text-red-500">
                        {validation.errors.contact_email}
                      </p>
                    ) : null}
                  </div>
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="contact_phone"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Phone*
                    </label>
                    <input
                      type="text"
                      id="contact_phone"
                      name="contact_phone"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter phone"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.contact_phone}
                    />
                    {validation.touched.contact_phone &&
                    validation.errors.contact_phone ? (
                      <p className="text-red-500">
                        {validation.errors.contact_phone}
                      </p>
                    ) : null}
                  </div>
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="gender"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Gender*
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.gender}
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {validation.touched.gender && validation.errors.gender ? (
                      <p className="text-red-500">{validation.errors.gender}</p>
                    ) : null}
                  </div>
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="password"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Password{isEdit ? "" : "*"}
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder={isEdit ? "Enter new password (optional)" : "Enter password"}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.password}
                    />
                    {validation.touched.password && validation.errors.password ? (
                      <p className="text-red-500">{validation.errors.password}</p>
                    ) : null}
                  </div>
                  <div className="xl:col-span-12">
                    <div className="flex justify-end gap-2 mt-4">
                      <Link
                        to="/"
                        className="text-red-500 bg-red-100 btn hover:text-white hover:bg-red-600 focus:text-white focus:bg-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:ring active:ring-red-100 dark:bg-red-500/20 dark:text-red-500 dark:hover:bg-red-500 dark:hover:text-white dark:focus:bg-red-500 dark:focus:text-white dark:active:bg-red-500 dark:active:text-white dark:ring-red-400/20"
                      >
                        Cancel
                      </Link>
                      <button
                        type="submit"
                        disabled={validation.isSubmitting || loading}
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                      >
                        {validation.isSubmitting || loading ? (
                          <TailSpin
                            visible={true}
                            height="20"
                            width="20"
                            color="#fff"
                            ariaLabel="tail-spin-loading"
                            radius="1"
                            wrapperStyle={{}}
                            wrapperClass=""
                          />
                        ) : isEdit ? (
                          "Update Admin"
                        ) : (
                          "Add Admin"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AdminForm;
